import React, { useMemo } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, PieChart, Pie, Cell, LabelList,
} from 'recharts';
import { PharmacyDistRow } from '../../types';
import { fmtSum, fmtSumShort, rainbowColor } from '../../utils';
import { ChartTooltip, PieTooltip } from './ChartTooltips';
import { TOP_N } from '../../constants';

interface PharmacyDistChartProps {
  data: PharmacyDistRow[];
}

/**
 * График 2: Распределение суммы по аптекам.
 * Левая часть — круговая диаграмма (Pie) с долями топ-N аптек.
 * Правая часть — горизонтальный рейтинг (Bar).
 * Снизу — легенда с суммами.
 */
const PharmacyDistChart: React.FC<PharmacyDistChartProps> = ({ data }) => {
  const sorted = useMemo(() => [...data].sort((a, b) => b.total - a.total), [data]);

  const { segments, total } = useMemo(() => {
    const topCount = Math.min(TOP_N, sorted.length);
    const top = sorted.slice(0, TOP_N).map((r, i) => ({
      name: r.PharmacyNum,
      value: r.total,
      color: rainbowColor(i, topCount),
    }));
    const othersVal = sorted.slice(TOP_N).reduce((s, r) => s + r.total, 0);
    if (othersVal > 0) top.push({ name: `+${sorted.length - TOP_N} проч.`, value: othersVal, color: '#94a3b8' });
    const total = sorted.reduce((s, r) => s + r.total, 0);
    return { segments: top, total, allCount: sorted.length };
  }, [sorted]);

  const barData = useMemo(() =>
    segments
      .filter(s => !s.name.startsWith('+'))
      .map(s => ({ name: `№${s.name}`, value: s.value, color: s.color })),
    [segments]
  );

  if (segments.length === 0) {
    return <Typography color="text.secondary" textAlign="center" py={6}>Нет данных</Typography>;
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700}>Распределение суммы по аптекам</Typography>
        <Typography variant="caption" color="text.secondary">
          топ-{Math.min(TOP_N, sorted.length)} из {sorted.length} аптек · все периоды
        </Typography>
      </Box>

      <Grid container spacing={3} alignItems="flex-start">
        {/* Pie */}
        <Grid item xs={12} md={5}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}
            sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1 }}>
            Доля в общей сумме
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={segments} cx="50%" cy="50%"
                innerRadius={75} outerRadius={120}
                dataKey="value" nameKey="name" paddingAngle={2}
                label={({ name, percent }: any) =>
                  (percent ?? 0) > 0.04 ? `№${name} ${((percent ?? 0) * 100).toFixed(0)}%` : ''}
                labelLine={{ stroke: '#cbd5e1', strokeWidth: 1 }}
              >
                {segments.map(s => <Cell key={s.name} fill={s.color} stroke="white" strokeWidth={2} />)}
              </Pie>
              <RTooltip content={<PieTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <Typography textAlign="center" variant="body2" color="text.secondary" sx={{ mt: -1 }}>
            Итого: <strong>{fmtSum(total)}</strong>
          </Typography>
        </Grid>

        {/* Bar */}
        <Grid item xs={12} md={7}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}
            sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1 }}>
            Рейтинг аптек (топ-{Math.min(TOP_N, barData.length)})
          </Typography>
          <ResponsiveContainer width="100%" height={310}>
            <BarChart data={barData} layout="vertical" margin={{ top: 0, right: 80, left: 0, bottom: 0 }}>
              <CartesianGrid horizontal={false} stroke="#f1f5f9" strokeDasharray="4 4" />
              <XAxis type="number" tickFormatter={fmtSumShort}
                tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={60}
                tick={{ fontSize: 11, fill: '#64748b', fontWeight: 600 }} axisLine={false} tickLine={false} />
              <RTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(99,102,241,0.05)' }} />
              <Bar dataKey="value" name="Сумма" radius={[0, 6, 6, 0]} maxBarSize={22}>
                {barData.map(s => <Cell key={s.name} fill={s.color} />)}
                <LabelList dataKey="value" position="right" formatter={fmtSumShort as any}
                  style={{ fontSize: 11, fill: '#64748b', fontWeight: 600 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Grid>

        {/* Легенда */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            {segments.map(s => (
              <Box key={s.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: s.color }} />
                <Typography variant="caption" color="text.secondary">
                  {s.name.startsWith('+') ? s.name : `Аптека №${s.name}`}
                </Typography>
                <Typography variant="caption" fontWeight={700} sx={{ ml: 0.25 }}>{fmtSum(s.value)}</Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default PharmacyDistChart;
